import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  sampleSkyAuthoredNodes,
} from './115-sky-authored-pose.mjs';
import {
  stepSkyTwinTether,
  skyTwinSurfaceRole,
  skyTwinCraftWaypoint,
} from './130-sky-twin-tether.mjs';

export const SKY_TWINS = Object.freeze({
  health: 54,
  huntSeconds: 4,
  transferSeconds: 9,
  openingSeconds: 13,
  repairSeconds: 9,
  craftSpeed: 22,
});
const needed = [
  'Idle',
  'BankLeft',
  'BankRight',
  'CrossDive',
  'PowerTransfer',
  'Disabled',
  'Repair',
  'Defeat',
];
const pilot = (s) => ({ x: s.x, y: s.y, z: s.pilotZ ?? 1 });
const xyz = (a) => ({ x: a[0], y: a[1], z: a[2] });
const offsets = new WeakMap();
function socketOffset(rig) {
  if (!offsets.has(rig))
    offsets.set(rig, sampleSkyAuthoredPose(rig, 'Idle', 0).sockets.tether_socket);
  return offsets.get(rig);
}

/** Both original craft and the actual copper-horn gantry are mandatory. An
 * unrelated rig cannot satisfy readiness or manufacture a cable collider. */
export function installSkyTwinRig(state, rig) {
  if (!state || !rig || compileSkyAuthoredRig(rig) !== rig) return false;
  if (rig.worldId === 'executioner-crimson') {
    if (
      !needed.every((name) => rig.clips.some((c) => c.name === name)) ||
      !rig.sockets.some((s) => s.name === 'tether_socket')
    )
      return false;
  } else if (rig.worldId === 'twin-gantry') {
    if (
      !['horn_left_contact', 'horn_right_contact', 'aperture_center'].every((name) =>
        rig.sockets.some((s) => s.name === name),
      ) ||
      !rig.nodes.some((n) => n.name === 'horn_left_joint') ||
      !rig.nodes.some((n) => n.name === 'horn_right_joint')
    )
      return false;
  } else return false;
  state.authoredPropRigs ??= Object.create(null);
  state.authoredPropRigs[rig.worldId] = rig;
  return true;
}
function member(rig, index) {
  return {
    rig,
    index,
    hp: SKY_TWINS.health,
    phase: 'Idle',
    phaseAge: 0,
    disabledAt: null,
    repairs: 0,
    transform: {
      position: [index ? 0 : -24, 24, index ? -112 : -32],
      quaternion: [0, 1, 0, 0],
      scale: [1, 1, 1],
    },
    pose: null,
  };
}
function prop(state, rig, artVariant, transform) {
  return {
    id: state.nextId++,
    kind: 'authored-prop',
    worldId: rig.worldId,
    artVariant,
    authoredRig: rig,
    rootTransform: transform,
    hp: 1,
    maxHp: 1,
    age: 0,
    collisionEnabled: true,
  };
}
function setPose(item, m, clip, seconds, desired, dt, gantry = null) {
  if (desired && m.hp > 0) {
    const offset = socketOffset(m.rig),
      intended = [desired.x + offset.x, desired.y - offset.y, desired.z + offset.z],
      goal = gantry
        ? skyTwinCraftWaypoint(m.transform.position, intended, m.rig, gantry)
        : intended;
    const gap = Math.hypot(...goal.map((v, i) => v - m.transform.position[i])),
      step = Math.min(1, (SKY_TWINS.craftSpeed * dt) / (gap || 1));
    m.transform.position = goal.map(
      (v, i) => m.transform.position[i] + (v - m.transform.position[i]) * step,
    );
  }
  if (m.phase !== clip) {
    m.transition = m.pose;
    m.phase = clip;
    m.phaseAge = 0;
  } else m.phaseAge += dt;
  const duration = m.rig.clips.find((c) => c.name === clip).duration;
  m.pose = sampleSkyAuthoredPose(m.rig, clip, Math.min(duration, seconds), m.transform, {
    pose: m.transition,
    weight: Math.max(0, 1 - m.phaseAge / 0.25),
    overrides: ['Disabled', 'Repair', 'Defeat'].includes(clip) ? [] : (m.damageOverrides ?? []),
  });
  Object.assign(item, xyz(m.transform.position), {
    authoredPose: m.pose,
    rootTransform: m.transform,
    authoredClip: clip,
    twinIndex: m.index,
  });
  const core = m.pose.sockets.reactor_target;
  item.weakpoint = {
    ...core,
    id: `executioner-${m.index}-reactor`,
    label: m.index ? 'CRIMSON REACTOR' : 'IVORY REACTOR',
    radius: 2,
    criticalRadius: 1,
    exposed: clip === 'PowerTransfer' && seconds >= 1.5,
  };
  item.weakpoints = [item.weakpoint];
  item.body = {
    x: (m.pose.bounds.min[0] + m.pose.bounds.max[0]) / 2,
    y: (m.pose.bounds.min[1] + m.pose.bounds.max[1]) / 2,
    z: m.pose.bounds.max[2],
    radius: 14,
  };
  item.anatomy = {
    primary: item.weakpoint,
    targets: item.weakpoints,
    body: item.body,
    attackSocket: m.pose.sockets.gun_left,
  };
  item.collisionEnabled = m.hp > 0;
}

/** Bind the pair as two independently posed physical sources. Primary HP is
 * their aggregate, so a single disabled craft never awards a guardian clear. */
export function createSkyTwinExecutioners(state, boss) {
  const crimson = state.authoredPropRigs?.['executioner-crimson'],
    gantryRig = state.authoredPropRigs?.['twin-gantry'];
  if (
    boss?.worldId !== 'twin-executioners' ||
    !crimson ||
    !gantryRig ||
    !needed.every((name) => boss.authoredRig.clips.some((c) => c.name === name))
  )
    return false;
  const gantry = prop(state, gantryRig, 'twin-gantry', {
    position: [0, 0, -72],
    quaternion: [0, 0, 0, 1],
    scale: [1, 1, 1],
  });
  gantry.twinGantry = true;
  gantry.authoredPose = sampleSkyAuthoredPose(gantryRig, 'Idle', 0, gantry.rootTransform);
  Object.assign(gantry, xyz(gantry.rootTransform.position));
  const members = [member(boss.authoredRig, 0), member(crimson, 1)];
  const companion = prop(state, crimson, 'executioner-crimson', members[1].transform);
  companion.twinIndex = 1;
  boss.authoredProps = [companion, gantry];
  const horns = ['left', 'right'].map(
    (side) => gantry.authoredPose.sockets[`horn_${side}_contact`],
  );
  boss.twins = {
    phase: 'arrival',
    age: 0,
    clock: 0,
    cycle: 0,
    donor: 0,
    members,
    gantry,
    companion,
    horns,
    tether: {},
    wraps: 0,
    repairs: 0,
    fired: 0,
    lateralLimit: Math.max(...horns.map((h) => Math.abs(h.x))) + 14,
    repairRemaining: null,
  };
  boss.hp = boss.maxHp = SKY_TWINS.health * 2;
  boss.authoredFlightCeiling = Math.max(...horns.map((h) => h.y)) + 22;
  boss.arenaMotion = 'fixed';
  boss.attackKind = 'energy';
  if (state.startMode === 'boss') {
    state.y = horns[0].y + 12;
    state.locomotion = 'fly';
  }
  for (let i = 0; i < 2; i++) setPose(i ? companion : boss, members[i], 'Idle', 0, null, 0);
  return true;
}
function enter(state, phase, event) {
  const t = state.boss.twins;
  t.phase = phase;
  t.age = 0;
  t.fired = 0;
  if (phase === 'transfer') t.tether = {};
  event?.(
    state,
    'twins-phase',
    phase === 'opening'
      ? 'TETHER BROKEN · DISABLE BOTH BEFORE REPAIR'
      : phase === 'transfer'
        ? 'DONOR CANOPY OPEN · THREAD THE TETHER AROUND A COPPER HORN'
        : 'MIRRORED HUNT · WATCH THE CANNON TELL',
  );
}
function targetFor(t, index, state) {
  const side = t.donor ? 1 : -1,
    horn = t.horns[t.donor];
  if (t.phase === 'transfer') {
    if (index === t.donor) return { x: state.x + side * 6, y: state.y - 8, z: horn.z + 24 };
    return { x: horn.x + side * 16, y: horn.y + 1, z: horn.z - 48 };
  }
  if (t.phase === 'opening') return { x: index ? 17 : -17, y: horn.y - 4, z: index ? -42 : -20 };
  return {
    x: (index ? 1 : -1) * (18 + Math.sin(t.age * 1.2) * 6),
    y: horn.y - 9 + Math.sin(t.age * 0.8 + index * Math.PI) * 3,
    z: index ? -112 : -32,
  };
}

/** Deterministic synchronized hunt, physically threaded transfer and finite
 * repair window. Every pose consumed by contact is the pose displayed by220. */
export function stepSkyTwinExecutioners(state, dt, { event, fireAimed } = {}) {
  const boss = state.boss,
    t = boss.twins;
  t.clock += dt;
  t.age += dt;
  if (boss.defeated) {
    for (let i = 0; i < 2; i++)
      setPose(i ? t.companion : boss, t.members[i], 'Defeat', boss.deathAge ?? 0, null, dt);
    t.tether = {};
    return true;
  }
  if (t.phase === 'arrival' && t.age >= 4) enter(state, 'hunt', event);
  else if (t.phase === 'hunt' && t.age >= SKY_TWINS.huntSeconds) enter(state, 'transfer', event);
  else if (t.phase === 'transfer' && t.age >= SKY_TWINS.transferSeconds) {
    t.donor = 1 - t.donor;
    t.cycle++;
    enter(state, 'hunt', event);
  } else if (t.phase === 'opening' && t.age >= SKY_TWINS.openingSeconds) {
    t.donor = 1 - t.donor;
    t.cycle++;
    enter(state, 'hunt', event);
  }
  const gantryClip =
    t.phase === 'opening'
      ? 'BeamBreak'
      : t.phase === 'hunt'
        ? t.wraps
          ? 'Reset'
          : 'TrolleySweep'
        : 'Idle';
  const gantrySeconds =
    gantryClip === 'Idle' ? 0 : Math.min(t.age, gantryClip === 'TrolleySweep' ? 4 : 3);
  if (t.gantry.authoredClip !== gantryClip || t.gantry.clipSeconds !== gantrySeconds) {
    t.gantry.authoredPose = sampleSkyAuthoredPose(
      t.gantry.authoredRig,
      gantryClip,
      gantrySeconds,
      t.gantry.rootTransform,
    );
    t.gantry.authoredClip = gantryClip;
    t.gantry.clipSeconds = gantrySeconds;
    t.horns = ['left', 'right'].map(
      (side) => t.gantry.authoredPose.sockets[`horn_${side}_contact`],
    );
  }
  const disabled = t.members.filter((m) => m.hp <= 0);
  t.repairRemaining =
    disabled.length === 1
      ? Math.max(0, SKY_TWINS.repairSeconds - (t.clock - disabled[0].disabledAt))
      : null;
  if (disabled.length === 1 && t.repairRemaining === 0) {
    const m = disabled[0];
    m.hp = SKY_TWINS.health;
    m.disabledAt = null;
    m.repairs++;
    m.repairAt = t.clock;
    m.damageOverrides = [];
    t.repairs++;
    event?.(state, 'twins-repair', 'PARTNER RESTARTED · DISABLE BOTH WITHIN THE REPAIR WINDOW');
  }
  const side = t.donor ? 1 : -1,
    horn = t.horns[t.donor];
  t.routeTarget = { x: horn.x - side * 14, y: horn.y + 7.5, z: state.pilotZ ?? 1 };
  t.tensionTarget = {
    x: t.routeTarget.x + side * 30,
    y: t.routeTarget.y + 10,
    z: state.pilotZ ?? 1,
  };
  for (let i = 0; i < 2; i++) {
    const m = t.members[i],
      item = i ? t.companion : boss;
    let clip =
      (t.phase === 'transfer' && i === t.donor) || t.phase === 'opening'
        ? 'PowerTransfer'
        : t.phase === 'hunt'
          ? i
            ? 'BankRight'
            : 'BankLeft'
          : 'Idle';
    let seconds = t.age;
    if (m.hp <= 0) {
      clip = 'Disabled';
      seconds = t.clock - m.disabledAt;
    } else if (m.repairAt != null && t.clock - m.repairAt < 3) {
      clip = 'Repair';
      seconds = t.clock - m.repairAt;
    }
    setPose(item, m, clip, seconds, targetFor(t, i, state), dt, t.gantry);
    item.age = boss.age;
  }
  if (
    t.phase === 'transfer' &&
    stepSkyTwinTether(
      t.tether,
      t.gantry,
      t.members[t.donor].pose.sockets.tether_socket,
      t.members[1 - t.donor].pose.sockets.tether_socket,
      pilot(state),
      dt,
    )
  ) {
    t.wraps++;
    enter(state, 'opening', event);
  }
  if (t.phase === 'hunt') {
    const due = t.age >= 2.5 ? 2 : t.age >= 1.1 ? 1 : 0;
    while (t.fired < due) {
      const i = t.fired++,
        m = t.members[i];
      if (m.hp > 0)
        fireAimed?.(
          state,
          m.pose.sockets[i ? 'gun_right' : 'gun_left'],
          i ? 'single' : 'paired',
          boss,
        );
    }
  }
  const down = t.members.findIndex((m) => m.hp <= 0);
  t.repairLink =
    down >= 0 && t.members[1 - down].hp > 0
      ? {
          from: t.members[1 - down].pose.sockets.tether_socket,
          to: t.members[down].pose.sockets.reactor_target,
          progress: 1 - (t.repairRemaining ?? SKY_TWINS.repairSeconds) / SKY_TWINS.repairSeconds,
        }
      : null;
  t.tells =
    t.phase === 'hunt' && t.fired < 2
      ? [
          {
            from: t.members[t.fired].pose.sockets[t.fired ? 'gun_right' : 'gun_left'],
            to: pilot(state),
            precision: t.fired === 1,
            progress: t.fired ? Math.max(0, (t.age - 1.1) / 1.4) : t.age / 1.1,
          },
        ]
      : [];
  boss.hp = t.members.reduce((sum, m) => sum + m.hp, 0);
  boss.weakpoints = [boss.weakpoint, t.companion.weakpoint].filter((p, i) => t.members[i].hp > 0);
  boss.anatomy.targets = boss.weakpoints;
  boss.attackPhase = t.phase;
  boss.phaseAge = t.age;
  boss.controlHint =
    t.repairRemaining != null
      ? `PARTNER REPAIR ${t.repairRemaining.toFixed(1)}s · STRIKE THE OTHER REACTOR`
      : t.phase === 'opening'
        ? 'TETHER SEVERED · CLIMB ABOVE BOTH OPEN CANOPIES'
        : t.phase === 'transfer'
          ? t.tether.knot
            ? 'COPPER HORN CAUGHT · KEEP MOVING TO TENSION THE TETHER'
            : 'FOLLOW THE DONOR · THREAD THE CABLE AROUND A COPPER HORN'
          : 'MIRRORED HUNT · DODGE THE FLUSH, THEN THE PRECISION SHOT';
  return true;
}

/** Only a manual nearest-triangle hit on the original revealed reactor can
 * disable a craft. Autonomous fire, canopy armor and prop fire award nothing. */
export function resolveSkyTwinContact(state, target, shot, contact, effect) {
  const boss = state.boss,
    t = boss?.twins;
  if (!t) return null;
  if (target.twinGantry) return { handled: true, damage: 0, quality: 'armor' };
  if (target !== boss && target !== t.companion) return null;
  const m = t.members[target === boss ? 0 : 1];
  if (
    effect.autonomous ||
    m.hp <= 0 ||
    !target.weakpoint.exposed ||
    skyTwinSurfaceRole(target, contact) !== 'reactor'
  )
    return { handled: true, damage: 0, quality: 'armor' };
  const damage = Math.min(m.hp, effect.damage * 2.5);
  m.hp = Math.max(0, m.hp - damage);
  if (m.hp > 0) {
    const nodes = sampleSkyAuthoredNodes(m.rig, 'Disabled', 1.4 * (1 - m.hp / SKY_TWINS.health));
    const wing = m.rig.nodes.findIndex((node) => node.name === 'wing_left_joint');
    m.damageOverrides = [{ node: wing, ...nodes[wing] }];
  }
  if (m.hp === 0) {
    m.disabledAt = t.clock;
    t.repairRemaining = t.members.some((member) => member.hp > 0) ? SKY_TWINS.repairSeconds : null;
    boss.controlHint = t.repairRemaining
      ? `PARTNER REPAIR ${t.repairRemaining.toFixed(1)}s · STRIKE THE OTHER REACTOR`
      : 'BOTH EXECUTIONERS DISABLED';
  }
  boss.hp = t.members.reduce((sum, n) => sum + n.hp, 0);
  return { handled: true, damage, quality: 'critical' };
}
